/**
 * Trail ruins (vanilla trail_ruins): the buried remains of a mud-brick and
 * terracotta settlement. Only the top of the tower shows above the grass;
 * dig down and there are the stumps of rooms around a road of packed mud and
 * gravel, with a chest in the tower's base.
 */

import { AIR, TERRACOTTA_IDS } from '../../blocks';
import { Biome } from '../biomes';
import type { TerrainGenerator } from '../terrain';
import {
  block, type Builder, cell, F, int, centredPiece, type Piece, type Rng, seedOf, type StructureType,
} from './kit';

const BIOMES: ReadonlySet<Biome> = new Set([
  Biome.Taiga, Biome.SnowyTaiga, Biome.OldGrowthPineTaiga, Biome.OldGrowthSpruceTaiga,
  Biome.OldGrowthBirchForest, Biome.Jungle,
]);

const MUD_BRICK = block('Mud Bricks');
const WALL = cell.mix([
  [MUD_BRICK, 6], [block('Packed Mud'), 2], [TERRACOTTA_IDS.brown, 1], [TERRACOTTA_IDS.light_gray, 1],
  [TERRACOTTA_IDS.red, 1], [block('Cobblestone'), 1],
]);
const ROAD = cell.mix([[block('Packed Mud'), 2], [block('Gravel'), 1], [block('Coarse Dirt'), 1]]);

/** The ruin's floor sits 8 below the surface; walls stand 2..4 high, the tower 10. */
function buildRuins(b: Builder): void {
  const y = b.y - 8, r = b.rnd;
  // the road through the middle
  b.fill(0, y, 7, 22, y, 9, ROAD);
  // rooms either side of the road, their walls broken off at random heights
  for (const [u0, v0, u1, v1] of [[1, 1, 7, 5], [10, 0, 15, 5], [2, 11, 9, 16], [13, 11, 21, 15]]) {
    b.fill(u0, y, v0, u1, y, v1, MUD_BRICK);
    for (let u = u0; u <= u1; u++) {
      for (let v = v0; v <= v1; v++) {
        if (u !== u0 && u !== u1 && v !== v0 && v !== v1) { b.fill(u, y + 1, v, u, y + 3, v, AIR); continue; }
        b.fill(u, y + 1, v, u, y + 1 + int(r, 4), v, WALL);
      }
    }
  }
  // the tower, tall enough to break the surface
  b.room(17, y, 1, 21, y + 10, 5, WALL);
  b.fill(18, y + 10, 2, 20, y + 10, 4, AIR);
  b.chest(19, y + 1, 3, F.NV);
}

function trailRuins(g: TerrainGenerator, x: number, z: number, rnd: Rng): Piece[] | null {
  const c = g.columnClimate(x, z);
  if (!BIOMES.has(c.biome)) return null;
  return [centredPiece(x, z, int(rnd, 4), 23, 17, c.height, false, seedOf(rnd), buildRuins)];
}

export const TRAIL_RUINS: StructureType = {
  name: 'Trail Ruins', spacing: 34, separation: 8, salt: 83469867, reach: 1, generate: trailRuins,
};
